import { Building2, Users, DollarSign, Calendar, Clock } from 'lucide-react';
import { formatCurrency } from '@smartphone-tycoon/shared-ui';
import type { BuildingType } from '@/types/buildings';
import { buildingService } from '@/services/tycoon/buildingService';
import { getMonthName } from '@/config/gameConstants';

interface BuildingCardProps {
  building: BuildingType;
  cash: number;
  gameDate: { year: number; month: number };
  onPurchase: (buildingTypeId: string) => boolean;
}

export function BuildingCard({ building, cash, gameDate, onPurchase }: BuildingCardProps) {
  const canAfford = cash >= building.cost;
  const isUnlocked = buildingService.isUnlockDateReached(building.unlockDate, gameDate);

  const handlePurchase = () => {
    onPurchase(building.id);
  };

  const getCategoryStyles = (category: string) => {
    switch (category) {
      case 'headquarters':
        return {
          bg: 'bg-theme-warning/20',
          icon: 'text-theme-warning',
          text: 'text-theme-warning'
        };
      case 'office':
        return {
          bg: 'bg-theme-info/20',
          icon: 'text-theme-info',
          text: 'text-theme-info'
        };
      case 'factory':
        return {
          bg: 'bg-theme-success/20',
          icon: 'text-theme-success',
          text: 'text-theme-success'
        };
      case 'lab':
        return {
          bg: 'bg-theme-accent/20',
          icon: 'text-theme-accent',
          text: 'text-theme-accent'
        };
      default:
        return {
          bg: 'bg-theme-bg-tertiary',
          icon: 'text-theme-text-secondary',
          text: 'text-theme-text-secondary'
        };
    }
  };

  const categoryStyles = getCategoryStyles(building.category);

  return (
    <div className={`bg-theme-bg-content rounded-lg border p-6 relative ${
      !isUnlocked ? 'opacity-60' : ''
    }`}>
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-lg ${categoryStyles.bg}`}>
            <Building2 className={`w-6 h-6 ${categoryStyles.icon}`} />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-theme-text-primary">{building.name}</h3>
            <p className={`text-sm ${categoryStyles.text} capitalize`}>{building.category}</p>
          </div>
        </div>
        {!isUnlocked && (
          <div className="flex items-center gap-1 text-theme-text-muted">
            <Calendar className="w-4 h-4" />
            <span className="text-sm">Unlocks {getMonthName(building.unlockDate.month)} {building.unlockDate.year}</span>
          </div>
        )}
      </div>

      {/* Description */}
      <p className="text-theme-text-secondary text-sm mb-4">{building.description}</p>

      {/* Stats */}
      <div className="space-y-3 mb-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-theme-text-secondary">
            <Users className="w-4 h-4" />
            <span className="text-sm">Capacity</span>
          </div>
          <span className="font-medium">
            {building.capacity.officeWorkers + building.capacity.factoryWorkers + building.capacity.technicalWorkers} employees
          </span>
        </div>

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-theme-text-secondary">
            <DollarSign className="w-4 h-4" />
            <span className="text-sm">Maintenance</span>
          </div>
          <span className="font-medium">{formatCurrency(building.maintenanceCost)}/month</span>
        </div>

        {building.constructionTime > 0 && (
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-theme-text-secondary">
              <Clock className="w-4 h-4" />
              <span className="text-sm">Construction Time</span>
            </div>
            <span className="font-medium">{building.constructionTime} days</span>
          </div>
        )}

      </div>

      {/* Purchase Button */}
      <button
        onClick={handlePurchase}
        disabled={!isUnlocked || !canAfford}
        className={`w-full py-2 px-4 rounded-lg font-medium transition-colors ${
          isUnlocked && canAfford
            ? 'bg-theme-accent text-white hover:bg-theme-accent/80'
            : 'bg-theme-bg-tertiary text-theme-text-muted cursor-not-allowed'
        }`}
      >
        {!isUnlocked ? (
          `Unlocks ${getMonthName(building.unlockDate.month)} ${building.unlockDate.year}`
        ) : !canAfford ? (
          `Need ${formatCurrency(building.cost - cash)} more`
        ) : (
          `Purchase for ${formatCurrency(building.cost)}`
        )}
      </button>
    </div>
  );
}